import StockValuationPanel from '../modules/orders/stockValuation/StockValuationPanel';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Radio,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tabs,
  Timeline,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DollarOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  HomeOutlined,
  LinkOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  ShopOutlined,
  TruckOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { authService } from '../services/auth';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { formatUnit } from '../utils/units';
import { buildSafePdfFileName } from '../utils/pdfFileName';
import { getDisplayOrderNumber } from '../types/order';
import { usePageHeaderTitleOverride } from '../modules/navigation/pageHeaderTitleOverride';
import { ORDER_CURRENCIES } from '../modules/orders/currencies';
import {
  SUPPLIER_ORDER_PROGRESS_STATUSES,
  SUPPLIER_ORDER_STATUSES,
  SupplierOrderStatus,
} from '../modules/orders/supplierOrderStatuses';
import OrderStatusTransitionSelect from '../modules/orders/components/OrderStatusTransitionSelect';
import { buildCustomerOrderLinkOptions } from '../modules/orders/customerOrderLinkOptions';
import { FactoryOptionDto, SupplierOptionDto, UserOptionDto } from '../dto/reference';
import { OrderDto } from '../dto/order';
import {
  SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES,
  SupplierOrderCustomerOrderDto,
  SupplierOrderStatusHistoryDto,
  supplierOrderAmountRule,
  supplierOrderNumberRule,
  supplierOrderQuantityRule,
} from '../dto/supplierOrder';
import { fetchAccountManagerOptions, fetchFactoryOptions, fetchSupplierOptions } from '../modules/reference-data/api/referenceDataApi';
import { fetchOrders } from '../modules/orders/api/ordersApi';
import {
  createSupplierOrderDocument,
  deleteSupplierOrderDocument,
  downloadSupplierOrderDocumentFile,
  downloadSupplierOrderPdf,
  fetchSupplierOrderDocuments,
  getSupplierOrderDisplayNumber,
  linkCustomerOrder,
  SupplierOrderDocumentDto,
  unlinkCustomerOrder,
  updateSupplierOrder,
} from '../modules/orders/api/supplierOrdersApi';
import SupplierOrderDeliveryTransportsSection from '../modules/orders/sections/SupplierOrderDeliveryTransportsSection';
import SupplierOrderPZDocumentsSection from '../modules/orders/sections/SupplierOrderPZDocumentsSection';
import { useSupplierOrderDetail } from '../modules/orders/queries/useSupplierOrderDetail';
import {
  buildSupplierOrderUpdateData,
  getSupplierOrderFieldNames,
  getSupplierOrderInitialValues,
  SupplierOrderEditableField,
} from '../modules/orders/supplierOrderDetailEditUtils';
import {
  DETAIL_META_GRID_STYLE,
  getOrderTypeBadgeStyle,
  getOrderTypeHeaderStyle,
  getStatusBadgeStyle,
  INFO_GRID_STYLE,
  OrderWorkflowRail,
  PAGE_SHELL_STYLE,
  renderInfoItem,
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../modules/orders/orderDetailLayout';
import { SupplierOrderItemsSection } from '../modules/orders/sections/SupplierOrderItemsSection';
import {
  buildExistingSupplierOrderItemAllocations,
  calculateSupplierItemAllocationBalance,
  ExistingSupplierItemAllocationDraft,
  findInvalidSupplierItemAllocationIndex,
} from '../modules/orders/supplierOrderCreateMode';
import { promptOrderPdfConversionUnit } from '../modules/orders/orderPdfConversionPrompt';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';

const { Text } = Typography;

const formatCustomerAllocationQuantity = (record: SupplierOrderCustomerOrderDto) => {
  if (record.itemAllocations?.length) {
    return (
      <Space direction="vertical" size={2}>
        {record.itemAllocations.map((allocation) => (
          <span key={allocation.id}>
            {Number(allocation.allocatedQuantity).toLocaleString('pl-PL', { maximumFractionDigits: 3 })}
            {' '}{formatUnit(allocation.allocatedUnit)}
          </span>
        ))}
      </Space>
    );
  }
  if (!record.allocatedQuantity) return '-';

  const unit = record.allocatedUnit || record.supplierOrderItemUnit;
  return `${Number(record.allocatedQuantity).toLocaleString('pl-PL')} ${formatUnit(unit)}`.trim();
};

const SUPPLIER_ORDERS_LIST_PATH = '/app/orders?orderFlow=supplier';
/**
 * Supplier-order detail page. It reuses the customer-order layout primitives,
 * but copy and visible fields stay supplier-side: supplier, factory, purchase
 * documents, landed costs, and operational customer-order relations.
 */
const SupplierOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['orders', 'common', 'tasks']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [editForm] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [supplierDocumentForm] = Form.useForm();
  const selectedLinkCustomerOrderId = Form.useWatch('orderId', linkForm);

  const { supplierOrder, loading, refresh } = useSupplierOrderDetail(id);
  // Hoisted so every handler below (edit/status/link/document actions) can
  // name the order in its toast without re-deriving it — mirrors OrderDetail's
  // `orderDisplayNumber`. Guarded by `?` since it's read before the not-found check.
  const displayOrderNumber = supplierOrder ? getSupplierOrderDisplayNumber(supplierOrder) : '';

  usePageHeaderTitleOverride(
    supplierOrder
      ? t('orders:pageHeader.supplier', { number: getDisplayOrderNumber(supplierOrder) })
      : null,
  );

  const [editingField, setEditingField] = useState<SupplierOrderEditableField | null>(null);
  const [savingField, setSavingField] = useState<SupplierOrderEditableField | null>(null);
  const [numberForm] = Form.useForm();
  const [numberModalOpen, setNumberModalOpen] = useState(false);
  const numberFormUseManual = Form.useWatch('useManualNumbering', numberForm);
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [suppliers, setSuppliers] = useState<SupplierOptionDto[]>([]);
  const [factories, setFactories] = useState<FactoryOptionDto[]>([]);
  const [accountManagers, setAccountManagers] = useState<UserOptionDto[]>([]);
  const [customerOrders, setCustomerOrders] = useState<OrderDto[]>([]);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [documentLanguage, setDocumentLanguage] = useState<'en' | 'pl'>('pl');
  const [documentDownloading, setDocumentDownloading] = useState(false);
  const [supplierDocuments, setSupplierDocuments] = useState<SupplierOrderDocumentDto[]>([]);
  const [supplierDocumentsLoading, setSupplierDocumentsLoading] = useState(false);
  const [supplierDocumentModalVisible, setSupplierDocumentModalVisible] = useState(false);
  const [supplierDocumentSubmitting, setSupplierDocumentSubmitting] = useState(false);
  const [supplierDocumentDownloadingId, setSupplierDocumentDownloadingId] = useState<string | null>(null);

  const warehouseWorkflow = Boolean(authService.getCurrentUser()?.invoiceWorkflowEnabled);
  const canManage = authService.canManageSupplierOrders() && !supplierOrder?.commerciallyFrozenAt;
  const canManagePurchaseInvoices = warehouseWorkflow ? authService.canManageInvoiceImports() : authService.canManagePurchaseInvoices();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const canViewSupplierPurchasePrices = authService.canViewSupplierOrderPurchasePrices();
  const canDownloadSupplierOrderPdf = authService.canViewSupplierOrderPurchasePdf();
  const canEditSupplierPurchasePrices = authService.canEditSupplierOrderPurchasePrices() && !supplierOrder?.commerciallyFrozenAt;
  const canViewSupplierOrderFinance = authService.canViewSupplierOrderFinance();
  const canEditSupplierOrderFinance = authService.canEditSupplierOrderFinance() && !supplierOrder?.commerciallyFrozenAt;
  const canLinkCustomerOrders = !warehouseWorkflow && canManage && !supplierOrder?.stockPurchase;
  const useManualNumberingValue = Form.useWatch('useManualNumbering', editForm);

  const renderExchangeRateToPLN = () => {
    if (!supplierOrder?.exchangeRateToPLN) {
      return supplierOrder?.currency === 'PLN'
        ? <Text type="secondary">{t('orders:supplierOrders.detail.headerRateNotApplicable')}</Text>
        : '-';
    }

    const meta = [
      supplierOrder.exchangeRateToPLNEffectiveDate
        ? `${t('orders:supplierOrders.detail.exchangeRateEffectiveDate')}: ${formatDate(supplierOrder.exchangeRateToPLNEffectiveDate)}`
        : null,
      supplierOrder.exchangeRateToPLNSource || null,
      supplierOrder.exchangeRateToPLNTableNumber
        ? `${t('orders:supplierOrders.detail.exchangeRateTable')}: ${supplierOrder.exchangeRateToPLNTableNumber}`
        : null,
    ].filter(Boolean);

    return (
      <div style={{ textAlign: 'right' }}>
        <strong style={{ color: '#0f172a' }}>{supplierOrder.exchangeRateToPLN}</strong>
        {meta.length > 0 && (
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>
            {meta.join(' · ')}
          </div>
        )}
      </div>
    );
  };

  // Fetches the supplier-order PDF as a blob and triggers a browser download,
  // honoring the EN/PL document-language toggle.
  const handleDownloadDocument = async () => {
    if (!id || !supplierOrder || !canDownloadSupplierOrderPdf) return;

    const conversionChoice = await promptOrderPdfConversionUnit(modal, t);

    if (!conversionChoice) return;

    setDocumentDownloading(true);
    try {
      const orderBlob = await downloadSupplierOrderPdf(
        id,
        documentLanguage,
        conversionChoice === 'none' ? undefined : conversionChoice,
      );
      const url = window.URL.createObjectURL(new Blob([orderBlob]));
      const link = document.createElement('a');
      const filePrefix = documentLanguage === 'pl' ? 'zamowienie' : 'order';
      link.href = url;
      link.setAttribute('download', `${buildSafePdfFileName(filePrefix, displayOrderNumber)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(t('orders:orderDetail.orderDocumentDownloaded', { orderNumber: displayOrderNumber }));
    } catch (error) {
      feedback.error(t('orders:orderDetail.orderDocumentDownloadFailed', { orderNumber: displayOrderNumber }));
    } finally {
      setDocumentDownloading(false);
    }
  };

  const loadSupplierDocuments = async () => {
    if (!id || !canViewPurchaseDocuments) return;

    setSupplierDocumentsLoading(true);
    try {
      const rows = await fetchSupplierOrderDocuments(id);
      setSupplierDocuments(rows);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.documentsFetchFailed'));
    } finally {
      setSupplierDocumentsLoading(false);
    }
  };

  const openSupplierDocumentModal = () => {
    supplierDocumentForm.resetFields();
    supplierDocumentForm.setFieldsValue({
      documentType: 'SUPPLIER_INVOICE',
      currency: supplierOrder?.currency || 'PLN',
      document: [],
    });
    setSupplierDocumentModalVisible(true);
  };

  const handleCreateSupplierDocument = async () => {
    if (!id) return;

    let values: any = null;
    try {
      values = await supplierDocumentForm.validateFields();
      setSupplierDocumentSubmitting(true);
      const document = await createSupplierOrderDocument(id, values);
      feedback.success(t('orders:supplierOrders.detail.documentAdded', {
        number: document.number || document.fileName || '',
      }));
      setSupplierDocumentModalVisible(false);
      supplierDocumentForm.resetFields();
      await loadSupplierDocuments();
    } catch (error: any) {
      if (error?.errorFields) return;
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.documentAddFailed', undefined, {
        type: values?.documentType ? t(`orders:supplierOrders.documentTypes.${values.documentType}`) : '',
      }));
    } finally {
      setSupplierDocumentSubmitting(false);
    }
  };

  const handleDownloadSupplierDocument = async (record: SupplierOrderDocumentDto) => {
    setSupplierDocumentDownloadingId(record.id);
    try {
      const blob = await downloadSupplierOrderDocumentFile(record.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = record.fileName || `${record.number || record.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.documentDownloadFailed', undefined, {
        number: record.number || record.fileName || '',
      }));
    } finally {
      setSupplierDocumentDownloadingId(null);
    }
  };

  const handleDeleteSupplierDocument = (record: SupplierOrderDocumentDto) => {
    modal.confirm({
      title: t('orders:supplierOrders.detail.deleteDocumentConfirmTitle'),
      content: record.number || record.fileName,
      okText: t('common:delete'),
      okButtonProps: { danger: true },
      cancelText: t('common:cancel'),
      onOk: async () => {
        await deleteSupplierOrderDocument(record.id);
        feedback.success(t('orders:supplierOrders.detail.documentDeleted', {
          number: record.number || record.fileName || '',
        }));
        await loadSupplierDocuments();
      },
    });
  };

  useEffect(() => {
    if (!canManage) return;

    (async () => {
      try {
        const [supplierRows, factoryRows, accountManagerRows, customerOrderRows] = await Promise.all([
          fetchSupplierOptions(),
          fetchFactoryOptions(),
          fetchAccountManagerOptions('supplierOrderDetail.accountManagers'),
          warehouseWorkflow ? Promise.resolve([]) : fetchOrders({ isQuote: false }, 'supplierOrderDetail.customerOrders'),
        ]);
        setSuppliers(supplierRows);
        setFactories(factoryRows);
        setAccountManagers(accountManagerRows);
        setCustomerOrders(customerOrderRows);
      } catch {
        // Non-critical — selects fall back to the current value.
      }
    })();
  }, [canManage, warehouseWorkflow]);

  useEffect(() => {
    void loadSupplierDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canViewPurchaseDocuments]);

  const startEditing = (field: SupplierOrderEditableField) => {
    if (!supplierOrder) return;

    editForm.resetFields();
    setEditingField(field);
    editForm.setFieldsValue(getSupplierOrderInitialValues(supplierOrder, field));
  };

  const cancelEditing = (field: SupplierOrderEditableField) => {
    if (editingField !== field) return;

    setEditingField(null);
    editForm.resetFields();
  };

  const saveField = async (field: SupplierOrderEditableField) => {
    if (!id || !supplierOrder) return;
    if (field === 'currency' && !canEditSupplierPurchasePrices) return;
    if (field === 'landedCosts' && !canEditSupplierOrderFinance) return;

    try {
      const values = editForm.getFieldsValue(true);
      // recursive: true descends into Form.List entries (landedCosts items);
      // a bare ['landedCosts'] path would skip the per-item amount rules.
      await editForm.validateFields(getSupplierOrderFieldNames(field, values), { recursive: true });
      setSavingField(field);
      const updateData = buildSupplierOrderUpdateData(supplierOrder, field, values);

      await updateSupplierOrder(id, updateData);
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', { orderNumber: displayOrderNumber }));
      cancelEditing(field);
      await refresh();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', undefined, {
        orderNumber: displayOrderNumber,
      }));
    } finally {
      setSavingField(null);
    }
  };

  const saveOrderNumber = async () => {
    if (!id || !supplierOrder) return;
    try {
      const values = numberForm.getFieldsValue(true);
      await numberForm.validateFields(getSupplierOrderFieldNames('manualNumbering', values), { recursive: true });
      setSavingField('manualNumbering');
      await updateSupplierOrder(id, buildSupplierOrderUpdateData(supplierOrder, 'manualNumbering', values));
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', { orderNumber: displayOrderNumber }));
      setNumberModalOpen(false);
      await refresh();
    } catch (error: any) {
      if (error?.errorFields) return;
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', undefined, {
        orderNumber: displayOrderNumber,
      }));
    } finally {
      setSavingField(null);
    }
  };

  const getNumberModalInitialValues = (): Record<string, unknown> => {
    if (!supplierOrder) return {};
    const init = getSupplierOrderInitialValues(supplierOrder, 'manualNumbering');
    if (!supplierOrder.useManualNumbering) {
      init.manualOrderNumber = getSupplierOrderDisplayNumber(supplierOrder);
    }
    return init;
  };

  const openStatusModal = () => {
    if (!supplierOrder) return;
    const [firstAllowedStatus] = supplierOrder.allowedTransitions || [];
    statusForm.setFieldsValue({ status: firstAllowedStatus, statusNotes: '' });
    setStatusModalVisible(true);
  };

  const handleChangeStatus = async (values: any) => {
    if (!id || !supplierOrder) return;

    setStatusSaving(true);
    try {
      await updateSupplierOrder(
        id,
        {
          ...buildSupplierOrderUpdateData(supplierOrder, 'status', { status: values.status }),
          statusNotes: values.statusNotes?.trim() || null,
        }
      );
      feedback.success(t('orders:supplierOrders.detail.statusUpdateSuccess', {
        orderNumber: displayOrderNumber,
        status: t(`orders:supplierOrders.status.${values.status}`),
      }));
      setStatusModalVisible(false);
      statusForm.resetFields();
      await refresh();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.detail.statusUpdateError', undefined, {
        orderNumber: displayOrderNumber,
      }));
    } finally {
      setStatusSaving(false);
    }
  };

  const handleLinkCustomerOrder = async (values: any) => {
    if (!id || !canLinkCustomerOrders) return;

    const itemAllocations = (values.itemAllocations || []) as ExistingSupplierItemAllocationDraft[];
    const invalidAllocationIndex = findInvalidSupplierItemAllocationIndex(itemAllocations);
    if (invalidAllocationIndex >= 0) {
      const validationMessage = t('orders:supplierOrders.allocation.missingDimensions');
      linkForm.setFields([{
        name: ['itemAllocations', invalidAllocationIndex, 'customerQuantity'],
        errors: [validationMessage],
      }]);
      feedback.error(validationMessage);
      return;
    }

    const linkedCustomerOrder = customerOrders.find((order) => order.id === values.orderId);
    const linkedCustomerOrderNumber = linkedCustomerOrder ? getDisplayOrderNumber(linkedCustomerOrder) : '';

    setLinkSubmitting(true);
    try {
      await linkCustomerOrder(id, {
        orderId: values.orderId,
        notes: values.notes?.trim() || null,
        itemAllocations: itemAllocations.map((allocation) => ({
          orderProductId: allocation.orderProductId,
          supplierOrderItemId: allocation.supplierOrderItemId,
          customerQuantity: Number(allocation.customerQuantity),
          customerUnit: allocation.customerUnit,
          surplusDisposition: allocation.surplusDisposition || 'STOCK',
        })),
      });
      feedback.success(t('orders:supplierOrders.messages.linkSuccess', { customerOrderNumber: linkedCustomerOrderNumber }));
      setLinkModalVisible(false);
      linkForm.resetFields();
      await refresh();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.messages.linkError', undefined, {
        customerOrderNumber: linkedCustomerOrderNumber,
      }));
    } finally {
      setLinkSubmitting(false);
    }
  };

  const handleUnlinkCustomerOrder = async (record: SupplierOrderCustomerOrderDto) => {
    if (!canLinkCustomerOrders) return;
    if (!id) return;

    const customerOrderNumber = getDisplayOrderNumber(record);

    try {
      await unlinkCustomerOrder(id, record.orderId);
      feedback.success(t('orders:supplierOrders.messages.unlinkSuccess', { customerOrderNumber }));
      await refresh();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.messages.unlinkError', undefined, {
        customerOrderNumber,
      }));
    }
  };

  // Editable field for fields the supplier-order backend supports.
  const renderEditableField = (
    field: SupplierOrderEditableField,
    value: React.ReactNode,
    renderComponent: () => React.ReactNode
  ) => {
    if (!canManage || (field === 'invoice' && !canManagePurchaseInvoices)) return value;

    const isEditing = editingField === field;

    if (isEditing) {
      return (
        <div style={{ display: 'grid', gap: 10, width: '100%' }}>
          {renderComponent()}
          <Space wrap>
            <Button
              icon={<SaveOutlined />}
              size="small"
              type="primary"
              loading={savingField === field}
              onClick={() => saveField(field)}
            >
              {t('common:save')}
            </Button>
            <Button
              icon={<CloseOutlined />}
              size="small"
              onClick={() => cancelEditing(field)}
              disabled={savingField === field}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>{value}</div>
        <Tooltip title={t('orders:orderDetail.edit')}>
          <Button
            aria-label={t('orders:orderDetail.edit')}
            icon={<EditOutlined />}
            size="small"
            type="text"
            shape="circle"
            onClick={() => startEditing(field)}
            disabled={Boolean(editingField && editingField !== field)}
            style={{ flex: '0 0 auto', marginTop: -3, color: '#475569' }}
          />
        </Tooltip>
      </div>
    );
  };

  const statusHistoryEntries = useMemo<SupplierOrderStatusHistoryDto[]>(() => {
    if (!supplierOrder) return [];
    return Array.isArray(supplierOrder.statusHistory) && supplierOrder.statusHistory.length > 0
      ? supplierOrder.statusHistory
      : [{
        id: 'current',
        supplierOrderId: supplierOrder.id,
        status: supplierOrder.status,
        changedAt: supplierOrder.orderDate,
        notes: undefined,
      }];
  }, [supplierOrder]);

  const landedCostCategoryOptions = useMemo(
    () => SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES.map((cat) => ({
      value: cat,
      label: t(`orders:supplierOrders.landedCostCategory.${cat}`),
    })),
    [t]
  );

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!supplierOrder) {
    return (
      <div style={{ paddingBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(SUPPLIER_ORDERS_LIST_PATH)}
          style={{ marginBottom: 16, borderRadius: 999, fontWeight: 600 }}
        >
          {t('orders:supplierOrders.detail.backToList')}
        </Button>
        <Card style={SECTION_CARD_STYLE} bodyStyle={SECTION_CARD_BODY_STYLE}>
          {t('orders:supplierOrders.detail.notFound')}
        </Card>
      </div>
    );
  }

  const statusBadgeStyle = getStatusBadgeStyle(supplierOrder.status);
  const isFinancialCompletenessPending = supplierOrder.financialCompleteness?.status === 'PENDING';
  const purchasePricesIncomplete = Boolean(
    canViewSupplierPurchasePrices
      && supplierOrder.financialCompleteness?.missing?.includes('SUPPLIER_PURCHASE_PRICES')
  );
  const supplierOptions = suppliers.map((s) => ({ value: s.id, label: s.name, searchText: s.searchText }));
  const factoryOptions = factories.map((f) => ({ value: f.id, label: f.name }));
  const accountManagerOptions = accountManagers.map((manager) => ({
    value: manager.id,
    label: manager.name + (manager.email ? ` (${manager.email})` : ''),
  }));
  const customerOrderOptions = buildCustomerOrderLinkOptions(customerOrders);
  const linkedCustomerOrderIds = new Set((supplierOrder.customerOrders || []).map((order) => order.orderId));
  const availableCustomerOrderOptions = customerOrderOptions.filter((option) => !linkedCustomerOrderIds.has(option.value));
  const selectedLinkCustomerOrder = customerOrders.find((order) => order.id === selectedLinkCustomerOrderId);
  const linkItemMatch = selectedLinkCustomerOrder
    ? buildExistingSupplierOrderItemAllocations(selectedLinkCustomerOrder, supplierOrder.items || [])
    : { allocations: [], unmatchedCustomerProductNames: [] };
  const handleLinkCustomerOrderSelection = (orderId?: string) => {
    const customerOrder = customerOrders.find((order) => order.id === orderId);
    const match = customerOrder
      ? buildExistingSupplierOrderItemAllocations(customerOrder, supplierOrder.items || [])
      : { allocations: [] };
    linkForm.setFieldValue('itemAllocations', match.allocations);
  };
  const openLinkCustomerOrderModal = () => {
    linkForm.resetFields();
    linkForm.setFieldValue('itemAllocations', []);
    setLinkModalVisible(true);
  };
  const supplierPurchaseCostPLN = Number(supplierOrder.totalAmountPLN || 0);
  const supplierLandedCostPLN = Number(supplierOrder.landedCostAmountPLN || 0);
  const supplierCostBasisPLN = supplierPurchaseCostPLN + supplierLandedCostPLN;
  const supplierOrderLineCurrencies = Array.from(new Set(
    (supplierOrder.items || [])
      .map((item) => item.currency || supplierOrder.currency || 'PLN')
      .filter(Boolean)
  )).sort();
  const supplierOrderLineCurrenciesLabel = supplierOrderLineCurrencies.length > 0
    ? supplierOrderLineCurrencies.join(', ')
    : '-';
  const allowedStatusTransitions = supplierOrder.allowedTransitions || [];
  const currentProgressStatus = supplierOrder.status as SupplierOrderStatus;
  const supplierAccentColor = '#1d4ed8';
  const supplierAccentBackground = '#eff6ff';
  const isSupplierWorkflowCancelled = supplierOrder.status === 'CANCELLED';
  const isSupplierWorkflowHeld = supplierOrder.status === 'ON_HOLD';
  const supplierWorkflowSteps = SUPPLIER_ORDER_PROGRESS_STATUSES.map((status) => ({
    key: status,
    label: t(`orders:supplierOrders.status.${status}`),
  }));
  const supplierWorkflowCurrentLabel = t(
    isSupplierWorkflowCancelled
      ? 'orders:supplierOrders.detail.cancelledWorkflow'
      : isSupplierWorkflowHeld
        ? 'orders:supplierOrders.detail.heldWorkflow'
        : 'orders:supplierOrders.detail.currentStatus'
  );

  const landedCostsDisplay = (
    <div style={{ display: 'grid', gap: 8, width: '100%' }}>
      {!supplierOrder.landedCosts || supplierOrder.landedCosts.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('orders:supplierOrders.detail.noLandedCosts')}
        </Text>
      ) : (
        supplierOrder.landedCosts.map((item, idx) => (
          <div key={item.id || idx}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#475569', fontSize: 14 }}>
              <div>
                <span>{t(`orders:supplierOrders.landedCostCategory.${item.category}`)}</span>
                {item.note && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{item.note}</Text>
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <strong style={{ color: '#0f172a' }}>
                  {formatCurrency(item.originalAmount ?? item.amount, item.originalCurrency || supplierOrder.currency)}
                </strong>
                {item.originalCurrency && item.originalCurrency !== 'PLN' && item.amountPLN != null && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatCurrency(item.amountPLN, 'PLN')}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      )}
      <Divider style={{ margin: '2px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 15, fontWeight: 700 }}>
        <span>{t('orders:supplierOrders.detail.landedCostAmount')}</span>
        <strong style={{ color: '#0f172a' }}>{formatCurrency(supplierLandedCostPLN, 'PLN')}</strong>
      </div>
    </div>
  );

  const landedCostsForm = () => (
    <Form form={editForm} layout="vertical" style={{ width: '100%' }}>
      <Form.List name="landedCosts">
        {(fields, { add, remove }) => (
          <div style={{ display: 'grid', gap: 8 }}>
            {fields.map((field) => (
              <Row key={field.key} gutter={[8, 0]} align="top" style={{ flexWrap: 'nowrap' }}>
                <Col flex="180px">
                  <Form.Item
                    name={[field.name, 'category']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostCategory') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ required: true }]}
                  >
                    <Select options={landedCostCategoryOptions} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col flex="120px">
                  <Form.Item
                    name={[field.name, 'amount']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostAmount') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ ...supplierOrderAmountRule(t), required: true }]}
                  >
                    {/* No min/max clamp: an out-of-range entry must stay visible and
                        fail validation instead of being silently coerced on blur. */}
                    <InputNumber
                      precision={2}
                      style={{ width: '100%' }}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    />
                  </Form.Item>
                </Col>
                <Col flex="96px">
                  <Form.Item
                    name={[field.name, 'currency']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostCurrency') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ required: true }]}
                  >
                    <Select
                      style={{ width: '100%' }}
                      options={ORDER_CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
                    />
                  </Form.Item>
                </Col>
                <Col flex="auto">
                  <Form.Item
                    name={[field.name, 'note']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostNote') : undefined}
                    style={{ marginBottom: 8 }}
                  >
                    <Input maxLength={500} placeholder={t('orders:supplierOrders.form.landedCostNote')} />
                  </Form.Item>
                </Col>
                <Col flex="32px">
                  <Form.Item
                    label={fields.indexOf(field) === 0 ? ' ' : undefined}
                    style={{ marginBottom: 8 }}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() => add({ category: 'CUSTOMS_DUTY', amount: 0, currency: supplierOrder.currency || 'PLN', note: '' })}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              {t('orders:supplierOrders.form.addLandedCost')}
            </Button>
          </div>
        )}
      </Form.List>
    </Form>
  );

  const overviewMeta = [
    {
      icon: <ShopOutlined />,
      label: t('orders:supplierOrders.form.supplier'),
      value: supplierOrder.supplier?.name || '-',
    },
    {
      icon: <HomeOutlined />,
      label: t('orders:supplierOrders.form.factory'),
      value: supplierOrder.factory?.name || '-',
    },
    {
      icon: <CalendarOutlined />,
      label: t('orders:supplierOrders.form.orderDate'),
      value: supplierOrder.orderDate ? formatDate(supplierOrder.orderDate) : '-',
    },
    {
      icon: <TruckOutlined />,
      label: t('orders:supplierOrders.form.expectedDelivery'),
      value: supplierOrder.expectedDelivery ? formatDate(supplierOrder.expectedDelivery) : '-',
    },
  ];

  const linkedColumns = [
    {
      title: t('orders:supplierOrders.table.customerOrderNumber'),
      key: 'orderNumber',
      render: (_value: unknown, record: SupplierOrderCustomerOrderDto) => (
        <Link to={`/app/orders/${record.orderId}`}>
          {getDisplayOrderNumber(record)}
        </Link>
      ),
    },
    {
      title: t('orders:orderDetail.customer'),
      key: 'customer',
      render: (_value: unknown, record: SupplierOrderCustomerOrderDto) => {
        if (!record.customerName) return '-';
        if (!record.customerId) return record.customerName;

        return <Link to={`/app/customers/${record.customerId}`}>{record.customerName}</Link>;
      },
    },
    {
      title: t('orders:supplierOrders.form.supplierOrderItem'),
      key: 'supplierOrderItem',
      render: (_value: unknown, record: SupplierOrderCustomerOrderDto) => {
        if (record.itemAllocations?.length) {
          return (
            <Space direction="vertical" size={2}>
              {record.itemAllocations.map((allocation) => (
                <span key={allocation.id}>{allocation.supplierOrderItemName || '-'}</span>
              ))}
            </Space>
          );
        }
        if (!record.supplierOrderItemName) return '-';
        if (!record.supplierOrderItemProductId) return record.supplierOrderItemName;

        return (
          <Link to={`/app/products/${record.supplierOrderItemProductId}`}>
            {record.supplierOrderItemName}
          </Link>
        );
      },
    },
    {
      title: t('orders:supplierOrders.form.allocatedQuantity'),
      key: 'allocatedQuantity',
      align: 'right' as const,
      render: (_value: unknown, record: SupplierOrderCustomerOrderDto) => formatCustomerAllocationQuantity(record),
    },
    ...(canViewSupplierOrderFinance
      ? [
          {
            title: t('orders:supplierOrders.form.allocatedCostPLN'),
            dataIndex: 'effectiveAllocatedCostPLN',
            key: 'allocatedCostPLN',
            align: 'right' as const,
            render: (value?: number, record?: SupplierOrderCustomerOrderDto) => {
              const isManualAllocation = Number(record?.allocatedCostPLN || 0) > 0
                && record?.effectiveAllocatedPurchaseCostPLN === undefined
                && record?.effectiveAllocatedLandedCostPLN === undefined;

              return (
                <div style={{ display: 'grid', gap: 2 }}>
                  <strong>{formatCurrency(value || 0, 'PLN')}</strong>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {isManualAllocation
                      ? t('orders:supplierOrders.detail.manualAllocation')
                      : t('orders:supplierOrders.detail.allocationBreakdown', {
                        purchase: formatCurrency(record?.effectiveAllocatedPurchaseCostPLN || 0, 'PLN'),
                        landed: formatCurrency(record?.effectiveAllocatedLandedCostPLN || 0, 'PLN'),
                      })}
                  </Text>
                </div>
              );
            },
          },
        ]
      : []),
    ...(canLinkCustomerOrders
      ? [
          {
            title: '',
            key: 'actions',
            width: 56,
            align: 'right' as const,
            render: (_value: unknown, record: SupplierOrderCustomerOrderDto) => (
              <Tooltip title={t('common:delete')}>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleUnlinkCustomerOrder(record)}
                />
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ paddingBottom: 24 }}>
      <div style={PAGE_SHELL_STYLE}>
        {/* Overview header — clone of OrderDetail's. */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          style={getOrderTypeHeaderStyle(supplierAccentColor)}
        >
          <div style={{ flex: '1 1 100%', minWidth: 0 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(SUPPLIER_ORDERS_LIST_PATH)}
              style={{ marginBottom: 12, borderRadius: 999, fontWeight: 600 }}
            >
              {t('orders:supplierOrders.detail.backToList')}
            </Button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={getOrderTypeBadgeStyle(supplierAccentColor, supplierAccentBackground)}>
                <ShopOutlined />
                {t('orders:supplierOrders.detail.title')}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                {t('orders:orderDetail.title')}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
	              <div style={{ fontSize: 'clamp(1.45rem, 2.8vw, 2.3rem)', lineHeight: 1.05, fontWeight: 800, letterSpacing: 0, color: '#0f172a', overflowWrap: 'anywhere' }}>
	                {displayOrderNumber}
	              </div>
              {supplierOrder.commerciallyFrozenAt && <Tag>{t('invoices:workflow.frozenOrder')}</Tag>}
              {authService.canManageInvoiceImports() && <Button onClick={() => navigate(`/app/purchase-invoices/new?supplierOrderId=${supplierOrder.id}`)}>{t('invoices:workflow.page.createDraft')}</Button>}
              {canManage && (
                <Tooltip title={t('orders:orderDetail.editNumber')}>
                  <Button
                    aria-label={t('orders:orderDetail.editNumber')}
                    icon={<EditOutlined />}
                    type="text"
                    size="small"
                    shape="circle"
                    onClick={() => setNumberModalOpen(true)}
                    style={{ color: '#475569' }}
                  />
                </Tooltip>
              )}
              <Modal
                open={numberModalOpen}
                title={t('orders:orderDetail.editNumber')}
                onCancel={() => setNumberModalOpen(false)}
                onOk={saveOrderNumber}
                okText={t('common:save')}
                cancelText={t('common:cancel')}
                confirmLoading={savingField === 'manualNumbering'}
                destroyOnClose
              >
                <Form form={numberForm} layout="vertical" initialValues={getNumberModalInitialValues()}>
                  <Form.Item
                    name="useManualNumbering"
                    valuePropName="checked"
                    label={t('orders:orderDetail.useManualNumbering')}
                  >
                    <Switch />
                  </Form.Item>
                  {numberFormUseManual && (
                    <Form.Item
                      name="manualOrderNumber"
                      label={t('orders:supplierOrders.form.manualOrderNumber')}
                      rules={[{
                        required: true,
                        whitespace: true,
                        message: t('orders:modal.validation.manualNumberRequired'),
                      }, supplierOrderNumberRule(t)]}
                    >
                      <Input maxLength={64} placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')} />
                    </Form.Item>
                  )}
                </Form>
              </Modal>
              {supplierOrder.useManualNumbering && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '5px 10px',
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 12,
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                }}>
                  {t('orders:orderDetail.manual')}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <span style={statusBadgeStyle}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: 'currentColor' }} />
                {t(`orders:supplierOrders.status.${supplierOrder.status}`)}
              </span>
              {isFinancialCompletenessPending && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    color: '#92400e',
                  }}
                >
                  {t('orders:supplierOrders.detail.financialCompletenessPending')}
                </span>
              )}
              {canViewSupplierPurchasePrices && supplierOrder.currency && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                    background: '#ffffff',
                    border: '1px solid #dbe7f3',
                    color: '#334155',
                  }}
                >
                  {t('orders:supplierOrders.detail.summaryCurrencyValue', { currency: supplierOrder.currency })}
                </span>
              )}
              {canViewSupplierPurchasePrices && supplierOrderLineCurrencies.length > 0 && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 13,
                    background: '#eef6ff',
                    border: '1px solid #bfdbfe',
                    color: '#1d4ed8',
                  }}
                >
                  {t('orders:supplierOrders.detail.purchaseLineCurrenciesValue', {
                    currencies: supplierOrderLineCurrenciesLabel,
                  })}
                </span>
              )}
              {canManage && (
                <Button
                  type="primary"
                  onClick={openStatusModal}
                  style={{ borderRadius: 999, fontWeight: 600 }}
                >
                  {t('orders:orderDetail.updateStatus')}
                </Button>
              )}
              {canDownloadSupplierOrderPdf && (
                <div
                  style={{
	                    display: 'inline-flex',
	                    alignItems: 'center',
	                    flexWrap: 'wrap',
	                    gap: 8,
	                    padding: '6px 8px',
	                    borderRadius: 999,
	                    background: '#ffffff',
	                    border: '1px solid #dbe7f3',
	                    maxWidth: '100%',
	                  }}
	                >
                  <Segmented
                    size="small"
                    value={documentLanguage}
                    onChange={(value) => setDocumentLanguage(value as 'en' | 'pl')}
                    options={[
                      { label: 'EN', value: 'en' },
                      { label: 'PL', value: 'pl' },
                    ]}
                  />
	                  <Button
	                    icon={<FilePdfOutlined />}
	                    onClick={handleDownloadDocument}
	                    loading={documentDownloading}
	                    style={{ borderRadius: 999, fontWeight: 600, maxWidth: '100%', whiteSpace: 'normal', height: 'auto' }}
	                  >
	                    {t('orders:supplierOrders.detail.downloadSupplierOrderDocument')}
	                  </Button>
                </div>
              )}
            </div>

            <div style={DETAIL_META_GRID_STYLE}>
              {overviewMeta.map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '12px 14px',
                    borderRadius: 14,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div style={{ fontSize: 15, color: '#2563eb', marginTop: 1 }}>{item.icon}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ color: '#0f172a', fontSize: 15, fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word' }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <OrderWorkflowRail
          title={t('orders:supplierOrders.detail.workflow')}
          steps={supplierWorkflowSteps}
          currentKey={currentProgressStatus}
          currentLabel={supplierWorkflowCurrentLabel}
          cancelled={isSupplierWorkflowCancelled}
          cancelledText={t('orders:supplierOrders.detail.cancelledWorkflow')}
          held={isSupplierWorkflowHeld}
          heldText={t('orders:supplierOrders.detail.heldWorkflow')}
        />

        <Tabs
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          size="large"
          style={{ maxWidth: '100%', minWidth: 0 }}
          tabBarStyle={{ marginBottom: 24, fontWeight: 600, maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}
          items={[
            // ───────────────── Tab 1: Informacje ─────────────────
            {
              key: '1',
              label: t('orders:orderDetail.orderInfo'),
              children: (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  {(supplierOrder.stockPurchase || (supplierOrder.commerciallyFrozenAt && authService.getCurrentUser()?.invoiceWorkflowEnabled)) && canViewSupplierPurchasePrices && (
                    <StockValuationPanel supplierOrderId={supplierOrder.id} canEdit={authService.canEditSupplierOrderPurchasePrices()} onSaved={refresh} />
                  )}
                  {/* Items card: primary operational surface. */}
                  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
                    <SupplierOrderItemsSection
                      supplierOrder={supplierOrder}
                      canEdit={canManage}
                      canViewSupplierPurchasePrices={canViewSupplierPurchasePrices}
                      purchasePricesIncomplete={purchasePricesIncomplete}
                      onSaved={refresh}
                      onOpenTransports={() => setActiveTabKey('3')}
                    />
                  </motion.div>

                  {/* Card: Informacje */}
                  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}>
                    <Card
                      title={renderSectionTitle(t('orders:orderDetail.title'))}
                        style={{ ...SECTION_CARD_STYLE, marginBottom: 16 }}
                        headStyle={SECTION_CARD_HEAD_STYLE}
                        bodyStyle={SECTION_CARD_BODY_STYLE}
                      >
                        <div style={INFO_GRID_STYLE}>
                          {canViewSupplierPurchasePrices && renderInfoItem(
                            t('orders:supplierOrders.detail.summaryCurrency'),
                            supplierOrder.currency || '-'
                          )}
                          {renderInfoItem(t('orders:orderDetail.createdBy'), supplierOrder.createdBy?.name || '-')}
                          {renderInfoItem(
                            t('orders:orderDetail.accountManager'),
                            renderEditableField('accountManagerId', supplierOrder.accountManager?.name || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="accountManagerId" style={{ margin: 0 }}>
                                  <Select
                                    style={{ width: 260 }}
                                    placeholder={t('orders:modal.selectAccountManager')}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={accountManagerOptions}
                                  />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.orderDate'),
                            renderEditableField('orderDate', supplierOrder.orderDate ? formatDate(supplierOrder.orderDate) : '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="orderDate" style={{ margin: 0 }} rules={[{ required: true }]}>
                                  <DatePicker />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.expectedDelivery'),
                            renderEditableField('expectedDelivery', supplierOrder.expectedDelivery ? formatDate(supplierOrder.expectedDelivery) : '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="expectedDelivery" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.actualDelivery'),
                            renderEditableField('actualDelivery', supplierOrder.actualDelivery ? formatDate(supplierOrder.actualDelivery) : '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="actualDelivery" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.estimatedCompletionDate'),
                            renderEditableField('estimatedCompletionDate', supplierOrder.estimatedCompletionDate ? formatDate(supplierOrder.estimatedCompletionDate) : '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="estimatedCompletionDate" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.description'),
                            renderEditableField('description', supplierOrder.description || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                                <Form.Item name="description" style={{ margin: 0 }}>
                                  <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                                </Form.Item>
                              </Form>
                            )),
                            2
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.notes'),
                            renderEditableField('notes', supplierOrder.notes || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                                <Form.Item name="notes" style={{ margin: 0 }}>
                                  <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                                </Form.Item>
                              </Form>
                            )),
                            2
                          )}
                        </div>
                      </Card>
                    </motion.div>

                    {/* Card: Produkcja i logistyka */}
                    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
                      <Card
                        title={renderSectionTitle(t('orders:orderDetail.productionLogistics'))}
                        style={{ ...SECTION_CARD_STYLE, marginBottom: 16 }}
                        headStyle={SECTION_CARD_HEAD_STYLE}
                        bodyStyle={SECTION_CARD_BODY_STYLE}
                      >
                        <div style={INFO_GRID_STYLE}>
                          {renderInfoItem(
                            t('orders:orderDetail.useManualNumbering'),
                            renderEditableField(
                              'manualNumbering',
                              supplierOrder.useManualNumbering ? t('common:yes') : t('common:no'),
                              () => (
                                <Form form={editForm} style={{ display: 'inline-block' }}>
                                  <Space wrap>
                                    <Form.Item name="useManualNumbering" valuePropName="checked" style={{ margin: 0 }}>
                                      <Switch />
                                    </Form.Item>
                                    {useManualNumberingValue && (
                                      <Form.Item
                                        name="manualOrderNumber"
                                        style={{ margin: 0 }}
                                        rules={[{
                                          required: true,
                                          whitespace: true,
                                          message: t('orders:modal.validation.manualNumberRequired'),
                                        }, supplierOrderNumberRule(t)]}
                                      >
                                        <Input
                                          maxLength={64}
                                          placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')}
                                          style={{ width: 220 }}
                                        />
                                      </Form.Item>
                                    )}
                                  </Space>
                                </Form>
                              )
                            ),
                            2
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.supplier'),
                            renderEditableField('supplierId', supplierOrder.supplier?.name || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="supplierId" style={{ margin: 0 }} rules={[{ required: true }]}>
                                  <Select
                                    style={{ width: 260 }}
                                    showSearch
                                    optionFilterProp="searchText"
                                    options={supplierOptions}
                                  />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.producer'),
                            renderEditableField('producer', supplierOrder.producer || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="producer" style={{ margin: 0 }}>
                                  <Input style={{ width: 260 }} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:supplierOrders.form.factory'),
                            renderEditableField('factoryId', supplierOrder.factory?.name || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="factoryId" style={{ margin: 0 }}>
                                  <Select
                                    style={{ width: 260 }}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={factoryOptions}
                                  />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.factoryLocation'),
                            renderEditableField('factoryLocation', supplierOrder.factoryLocation || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                                <Form.Item name="factoryLocation" style={{ margin: 0 }}>
                                  <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                                </Form.Item>
                              </Form>
                            )),
                            2
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.deliveryLocation'),
                            renderEditableField('deliveryLocation', supplierOrder.deliveryLocation || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                                <Form.Item name="deliveryLocation" style={{ margin: 0 }}>
                                  <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                                </Form.Item>
                              </Form>
                            )),
                            2
                          )}
                        </div>
                      </Card>
                    </motion.div>

                    {/* Card: Dokumenty dostawcy */}
                    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>
                      <Card
                        title={renderSectionTitle(t('orders:supplierOrders.detail.supplierDocuments'))}
                        style={{ ...SECTION_CARD_STYLE, marginBottom: 16 }}
                        headStyle={SECTION_CARD_HEAD_STYLE}
                        bodyStyle={SECTION_CARD_BODY_STYLE}
                      >
                        <div style={INFO_GRID_STYLE}>
                          {renderInfoItem(
                            t('orders:orderDetail.proformaNumber'),
                            renderEditableField('proforma', supplierOrder.proformaNumber || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="proformaNumber" style={{ margin: 0 }}>
                                  <Input placeholder={t('orders:orderDetail.number')} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.proformaDate'),
                            editingField === 'proforma' ? (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="proformaDate" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ) : (
                              supplierOrder.proformaDate ? formatDate(supplierOrder.proformaDate) : '-'
                            )
                          )}
                          {canViewSupplierPurchasePrices && renderInfoItem(
                            t('orders:supplierOrders.detail.supplierInvoiceNumber'),
                            renderEditableField('invoice', supplierOrder.invoiceNumber || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="invoiceNumber" style={{ margin: 0 }}>
                                  <Input placeholder={t('orders:orderDetail.number')} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {canViewSupplierPurchasePrices && renderInfoItem(
                            t('orders:supplierOrders.detail.supplierInvoiceDate'),
                            editingField === 'invoice' ? (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="invoiceDate" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ) : (
                              supplierOrder.invoiceDate ? formatDate(supplierOrder.invoiceDate) : '-'
                            )
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.waybillNumber'),
                            renderEditableField('waybill', supplierOrder.waybillNumber || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="waybillNumber" style={{ margin: 0 }}>
                                  <Input placeholder={t('orders:orderDetail.number')} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.waybillDate'),
                            editingField === 'waybill' ? (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="waybillDate" style={{ margin: 0 }}>
                                  <DatePicker allowClear />
                                </Form.Item>
                              </Form>
                            ) : (
                              supplierOrder.waybillDate ? formatDate(supplierOrder.waybillDate) : '-'
                            )
                          )}
                          {renderInfoItem(
                            t('orders:supplierOrders.form.supplierReference'),
                            renderEditableField('supplierReference', supplierOrder.supplierReference || '-', () => (
                              <Form form={editForm} style={{ display: 'inline-block' }}>
                                <Form.Item name="supplierReference" style={{ margin: 0 }}>
                                  <Input />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                          {renderInfoItem(
                            t('orders:orderDetail.clientOrderNumber'),
                            renderEditableField('clientOrderNumber', supplierOrder.clientOrderNumber || '-', () => (
                              <Form form={editForm}>
                                <Form.Item name="clientOrderNumber" style={{ margin: 0 }}>
                                  <Input style={{ width: 220 }} />
                                </Form.Item>
                              </Form>
                            ))
                          )}
                        </div>
                      </Card>
                    </motion.div>

	                </Space>
	              ),
	            },

            // ───────────────── Tab 2: Aktywność ─────────────────
            {
              key: 'activity',
              label: (
                <span>
                  <FileTextOutlined /> {t('orders:orderDetail.activity')}
                </span>
              ),
              children: (
                <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.03 }}>
                  <Card
                    title={renderSectionTitle(t('orders:orderDetail.statusHistory'))}
                    style={SECTION_CARD_STYLE}
                    headStyle={SECTION_CARD_HEAD_STYLE}
                    bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
                  >
                    <Timeline style={{ marginBottom: 0 }}>
                      {statusHistoryEntries.map((history) => (
                        <Timeline.Item key={history.id}>
                          <div>
                            <strong>{t(`orders:supplierOrders.status.${history.status}`)}</strong>
                            {history.changedAt && (
                              <div style={{ fontSize: 12, color: '#999' }}>{formatDateTime(history.changedAt)}</div>
                            )}
                            {history.changedByName && (
                              <div style={{ fontSize: 12, color: '#64748b' }}>
                                {history.changedByName}
                              </div>
                            )}
                            {history.notes && <div style={{ fontSize: 12 }}>{history.notes}</div>}
                          </div>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </Card>
                </motion.div>
              ),
            },

            // ───────────────── Tab 3: Powiązania ─────────────────
            {
              key: '3',
              label: (
                <span>
                  <LinkOutlined /> {t('orders:supplierOrders.relations')}
                </span>
              ),
              children: (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  {/* Card: Transporty dostawy (Etap 2 — transport przywozowy;
                      2026-08-11 przeniesione tu z zakładki Informacje, bez
                      duplikacji — jedna lokalizacja). "Which trucks are
                      bringing this in" belongs with the other cross-module
                      links, not the editable order fields. Read-only: the
                      links themselves are owned by the transport form. */}
                  <Card
                    title={renderSectionTitle(t('orders:supplierOrders.detail.deliveryTransports'))}
                    style={SECTION_CARD_STYLE}
                    headStyle={SECTION_CARD_HEAD_STYLE}
                    bodyStyle={SECTION_CARD_BODY_STYLE}
                    data-testid="supplier-order-delivery-transports"
                  >
                    <SupplierOrderDeliveryTransportsSection
                      supplierOrderId={id}
                      onOpenTransport={(transportId) => navigate(`/app/transports/${transportId}`)}
                    />
                  </Card>

                  {/* Card: Dokumenty PZ — the PZ documents already issued off
                      this ZD, read-only (owned by the PZ module). */}
                  <Card
                    title={renderSectionTitle(t('orders:supplierOrders.detail.pzDocuments'))}
                    style={SECTION_CARD_STYLE}
                    headStyle={SECTION_CARD_HEAD_STYLE}
                    bodyStyle={SECTION_CARD_BODY_STYLE}
                    data-testid="supplier-order-pz-documents"
                  >
                    <SupplierOrderPZDocumentsSection supplierOrderId={id} />
                  </Card>

                  {(!warehouseWorkflow || Boolean(supplierOrder.customerOrders?.length)) && <Card
                    title={renderSectionTitle(t(warehouseWorkflow ? 'orders:warehouseFlow.historicalRelations' : 'orders:supplierOrders.relations'))}
                    style={SECTION_CARD_STYLE}
                    headStyle={SECTION_CARD_HEAD_STYLE}
                    bodyStyle={SECTION_CARD_BODY_STYLE}
                    extra={
                      canLinkCustomerOrders ? (
                        <Button type="primary" size="small" icon={<LinkOutlined />} onClick={openLinkCustomerOrderModal}>
                          {t('orders:supplierOrders.addCustomerOrderRelation')}
                        </Button>
                      ) : null
                    }
                  >
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      {t(warehouseWorkflow ? 'orders:warehouseFlow.historicalRelationsHelp' : 'orders:supplierOrders.detail.relationsHelp')}
                    </Text>
                    <Table
                      rowKey={(record: SupplierOrderCustomerOrderDto) => record.linkId || record.orderId}
                      size="middle"
                      pagination={false}
                      dataSource={supplierOrder.customerOrders || []}
                      columns={linkedColumns}
                      locale={{ emptyText: <Text type="secondary">{t('orders:supplierOrders.detail.noLinkedCustomerOrders')}</Text> }}
                      summary={(data) =>
                        canViewSupplierOrderFinance && data.length ? (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={4}>
                              <strong>{t('orders:orderDetail.total')}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="right">
                              <strong>
                                {formatCurrency(
                                  data.reduce((sum, item) => sum + Number(item.effectiveAllocatedCostPLN || item.allocatedCostPLN || 0), 0),
                                  'PLN'
                                )}
                              </strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5} />
                          </Table.Summary.Row>
                        ) : null
                      }
                    />
                  </Card>}
                </Space>
              ),
            },

            // ───────────────── Tab 4: Finanse ─────────────────
            ...(canViewSupplierOrderFinance ? [{
              key: '4',
              label: (
                <span>
                  <DollarOutlined /> {t('orders:supplierOrders.detail.finances')}
                </span>
              ),
              children: (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  {canViewSupplierPurchasePrices && (
                    <Card
                      title={renderSectionTitle(t('orders:orderDetail.financialSummary'))}
                      style={SECTION_CARD_STYLE}
                      headStyle={SECTION_CARD_HEAD_STYLE}
                      bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
                    >
                      <Row gutter={[24, 16]}>
                        <Col xs={24} md={8}>
                          <div style={{ padding: 16, borderRadius: 16, background: 'rgba(248, 250, 252, 0.92)', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b', marginBottom: 8 }}>
                              {t('orders:supplierOrders.detail.supplierCostBasisPLN')}
                            </div>
                            <div style={{ fontSize: 28, lineHeight: 1.05, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                              {formatCurrency(supplierCostBasisPLN, 'PLN')}
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'grid', gap: 3 }}>
                              <span>{t('orders:supplierOrders.detail.summaryCurrency')}: {supplierOrder.currency || '-'}</span>
                              <span>{t('orders:supplierOrders.detail.purchaseLineCurrencies')}: {supplierOrderLineCurrenciesLabel}</span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} md={16}>
                          <div style={{ display: 'grid', gap: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#475569', fontSize: 15 }}>
                              <span>{t('orders:supplierOrders.detail.purchaseCostSummaryCurrency')}</span>
                              <strong style={{ color: '#0f172a' }}>{formatCurrency(supplierOrder.totalAmount || 0, supplierOrder.currency)}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#475569', fontSize: 15 }}>
                              <span>{t('orders:supplierOrders.detail.purchaseCostConvertedPLN')}</span>
                              <strong style={{ color: '#0f172a' }}>{formatCurrency(supplierPurchaseCostPLN, 'PLN')}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#475569', fontSize: 15 }}>
                              <span>{t('orders:supplierOrders.detail.purchaseLineCurrencies')}</span>
                              <strong style={{ color: '#0f172a' }}>{supplierOrderLineCurrenciesLabel}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#475569', fontSize: 15 }}>
                              <span>{t('orders:supplierOrders.detail.headerExchangeRate')}</span>
                              {renderExchangeRateToPLN()}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  )}

                  {canViewSupplierOrderFinance && (
                  <Card
                    title={renderSectionTitle(t('orders:supplierOrders.detail.landedCosts'))}
                    style={SECTION_CARD_STYLE}
                    headStyle={SECTION_CARD_HEAD_STYLE}
                    bodyStyle={{ ...SECTION_CARD_BODY_STYLE, paddingTop: 16 }}
                  >
                    {canEditSupplierOrderFinance
                      ? renderEditableField('landedCosts', landedCostsDisplay, landedCostsForm)
                      : landedCostsDisplay}
                  </Card>
                  )}

                  <Card
                    title={renderSectionTitle(t('orders:supplierOrders.detail.supplierInvoices'))}
                    extra={canManagePurchaseInvoices && (
                      <Button icon={<UploadOutlined />} onClick={openSupplierDocumentModal}>
                        {t('orders:supplierOrders.detail.addSupplierDocument')}
                      </Button>
                    )}
                    style={SECTION_CARD_STYLE}
                    headStyle={SECTION_CARD_HEAD_STYLE}
                    bodyStyle={SECTION_CARD_BODY_STYLE}
                  >
                    <Table
                      rowKey="id"
                      size="middle"
                      loading={supplierDocumentsLoading}
                      pagination={{ pageSize: 10, hideOnSinglePage: true }}
                      dataSource={supplierDocuments}
                      columns={[
                        {
                          title: t('orders:supplierOrders.detail.documentType'),
                          dataIndex: 'documentType',
                          key: 'documentType',
                          render: (value: string) => t(`orders:supplierOrders.documentTypes.${value}`),
                        },
                        {
                          title: t('orders:supplierOrders.detail.documentNumber'),
                          dataIndex: 'number',
                          key: 'number',
                          render: (value: string | null, record: SupplierOrderDocumentDto) => value || record.fileName,
                        },
                        {
                          title: t('orders:supplierOrders.detail.documentDate'),
                          dataIndex: 'documentDate',
                          key: 'documentDate',
                          render: (value?: string | null) => value ? formatDate(value) : '-',
                        },
                        {
                          title: t('orders:orderDetail.amount'),
                          key: 'amount',
                          align: 'right' as const,
                          render: (_: unknown, record: SupplierOrderDocumentDto) => (
                            record.amount != null ? formatCurrency(Number(record.amount), record.currency || 'PLN') : '-'
                          ),
                        },
                        {
                          title: t('orders:orderDetail.actions'),
                          key: 'actions',
                          width: 120,
                          render: (_: unknown, record: SupplierOrderDocumentDto) => (
                            <Space>
                              <Tooltip title={t('orders:supplierOrders.detail.downloadSupplierDocument')}>
                                <Button
                                  icon={<DownloadOutlined />}
                                  onClick={() => handleDownloadSupplierDocument(record)}
                                  loading={supplierDocumentDownloadingId === record.id}
                                />
                              </Tooltip>
                              {canManagePurchaseInvoices && (
                                <Tooltip title={t('common:delete')}>
                                  <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteSupplierDocument(record)} />
                                </Tooltip>
                              )}
                            </Space>
                          ),
                        },
                      ]}
                      locale={{ emptyText: t('orders:orderDetail.noPurchaseInvoices') }}
                    />
                  </Card>

                </Space>
              ),
            }] : []),

            // ───────────────── Tab 5: Zadania ─────────────────
            // Mirrors the customer Tasks tab: same centered empty-state copy
            // when there are no tasks. When data exists, the 5 columns mirror
            // customer's: taskTitle / status (Tag) / priority (Tag) /
            // assignee / dueDate (Tag with calendar icon).
            {
              key: '5',
              label: (
                <span>
                  <CheckSquareOutlined /> {t('tasks:orderDetailTab')}
                </span>
              ),
              children: (
                <Card
                  title={t('tasks:orderDetailTab')}
                  style={SECTION_CARD_STYLE}
                  headStyle={SECTION_CARD_HEAD_STYLE}
                  bodyStyle={SECTION_CARD_BODY_STYLE}
                >
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                    {t('tasks:noTasks')}
                  </div>
                </Card>
              ),
            },
          ]}
        />
      </div>

      {/* Supplier purchase document modal */}
      <Modal
        title={t('orders:supplierOrders.detail.addSupplierDocument')}
        open={supplierDocumentModalVisible}
        onCancel={() => setSupplierDocumentModalVisible(false)}
        onOk={handleCreateSupplierDocument}
        confirmLoading={supplierDocumentSubmitting}
        maskClosable={false}
      >
        <Form form={supplierDocumentForm} layout="vertical">
          <Form.Item name="documentType" label={t('orders:supplierOrders.detail.documentType')} rules={[{ required: true }]}>
            <Select>
              {['SUPPLIER_INVOICE', 'TRANSPORT_INVOICE', 'CUSTOMS_DOCUMENT', 'OTHER'].map((type) => (
                <Select.Option key={type} value={type}>
                  {t(`orders:supplierOrders.documentTypes.${type}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="number" label={t('orders:supplierOrders.detail.documentNumber')}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={12}>
              <Form.Item name="documentDate" label={t('orders:supplierOrders.detail.documentDate')}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="currency" label={t('orders:currency')} rules={[{ required: true }]}>
                <Select>
                  {ORDER_CURRENCIES.map((currency) => (
                    <Select.Option key={currency} value={currency}>{currency}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="amount"
            label={t('orders:orderDetail.amount')}
            rules={[supplierOrderAmountRule(t)]}
          >
            <InputNumber
              precision={2}
              style={{ width: '100%' }}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('orders:notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="document"
            label={t('orders:supplierOrders.detail.documentFile')}
            valuePropName="fileList"
            getValueFromEvent={(event) => Array.isArray(event) ? event : event?.fileList}
            rules={[{ required: true, message: t('orders:supplierOrders.detail.documentFileRequired') }]}
          >
            <Upload
              accept=".jpeg,.jpg,.png,.pdf,.doc,.docx,.xls,.xlsx"
              beforeUpload={() => false}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>{t('orders:supplierOrders.detail.selectDocumentFile')}</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Status modal */}
      <Modal
        title={t('orders:orderDetail.updateStatus')}
        open={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleChangeStatus}>
          <Form.Item name="status" label={t('orders:supplierOrders.form.status')} rules={[{ required: true }]}>
            <OrderStatusTransitionSelect
              statuses={SUPPLIER_ORDER_STATUSES}
              currentStatus={supplierOrder.status}
              allowedTransitions={allowedStatusTransitions}
              getStatusLabel={(status) => t(`orders:supplierOrders.status.${status}`, {
                defaultValue: t('common:unknownStatus'),
              })}
              currentLabel={t('orders:statusSelection.current')}
              unavailableLabel={t('orders:statusSelection.unavailable')}
            />
          </Form.Item>
          <Form.Item name="statusNotes" label={t('orders:orderDetail.notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={statusSaving}>
                {t('common:save')}
              </Button>
              <Button onClick={() => setStatusModalVisible(false)}>{t('common:cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Link customer-order modal */}
      <Modal
        title={t('orders:supplierOrders.linkCustomerOrder')}
        open={canLinkCustomerOrders && linkModalVisible}
        onCancel={() => setLinkModalVisible(false)}
        footer={null}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {t('orders:supplierOrders.allocation.automaticMatchHelp')}
        </Text>
        <Form form={linkForm} layout="vertical" onFinish={handleLinkCustomerOrder}>
          <Form.Item name="orderId" label={t('orders:supplierOrders.form.customerOrder')} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={availableCustomerOrderOptions}
              notFoundContent={t('orders:supplierOrders.detail.noAvailableCustomerOrders')}
              onChange={handleLinkCustomerOrderSelection}
            />
          </Form.Item>

          {linkItemMatch.unmatchedCustomerProductNames.length > 0 && (
            <Alert
              type={linkItemMatch.allocations.length > 0 ? 'warning' : 'error'}
              showIcon
              style={{ marginBottom: 16 }}
              message={linkItemMatch.allocations.length > 0
                ? t('orders:supplierOrders.allocation.unmatchedProducts', {
                    products: linkItemMatch.unmatchedCustomerProductNames.join(', '),
                  })
                : t('orders:supplierOrders.allocation.noMatchingProducts')}
            />
          )}

          <Form.List
            name="itemAllocations"
            rules={[{
              validator: async (_, allocations) => {
                if (!Array.isArray(allocations) || allocations.length === 0) {
                  throw new Error(t('orders:supplierOrders.allocation.noMatchingProducts'));
                }
              },
            }]}
          >
            {(fields, _operations, { errors }) => (
              <>
                {fields.map((field) => (
                  <Card key={field.key} size="small" style={{ marginBottom: 12 }}>
                    {[
                      'orderProductId', 'supplierOrderItemId', 'customerProductName', 'supplierItemName',
                      'quantity', 'unit', 'customerUnit', 'width', 'length', 'height', 'thickness',
                    ].map((name) => (
                      <Form.Item key={name} name={[field.name, name]} hidden>
                        <Input />
                      </Form.Item>
                    ))}

                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const allocation = getFieldValue(['itemAllocations', field.name]) as ExistingSupplierItemAllocationDraft;
                        const balance = calculateSupplierItemAllocationBalance(allocation || {});
                        const number = (value: number | null) => value == null
                          ? '—'
                          : value.toLocaleString('pl-PL', { maximumFractionDigits: 3 });
                        const hasSurplus = Number(balance.surplusInCustomerUnit || 0) > 0.0001;

                        return (
                          <div>
                            <Text strong style={{ display: 'block' }}>{allocation?.customerProductName || '—'}</Text>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                              {t('orders:supplierOrders.allocation.supplierLine')}: {allocation?.supplierItemName || '—'} · {number(allocation?.quantity || 0)} {formatUnit(allocation?.unit)}
                            </Text>
                            <Row gutter={[12, 12]}>
                              <Col xs={24} md={8}>
                                <Form.Item
                                  name={[field.name, 'customerQuantity']}
                                  label={t('orders:supplierOrders.allocation.customerTarget')}
                                  rules={[
                                    { required: true },
                                    supplierOrderQuantityRule(t),
                                    {
                                      validator: async (_, value) => {
                                        const nextBalance = calculateSupplierItemAllocationBalance({
                                          ...(allocation || {}),
                                          customerQuantity: Number(value || 0),
                                        });
                                        if (nextBalance.error) {
                                          throw new Error(t('orders:supplierOrders.allocation.missingDimensions'));
                                        }
                                      },
                                    },
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <InputNumber
                                    step={0.001}
                                    precision={3}
                                    addonAfter={formatUnit(allocation?.customerUnit)}
                                    style={{ width: '100%' }}
                                    {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8}>
                                <Text type="secondary">{t('orders:supplierOrders.allocation.purchaseEquivalent')}</Text>
                                <div style={{ fontWeight: 650, marginTop: 6 }}>
                                  {number(balance.supplierEquivalentInCustomerUnit)} {formatUnit(allocation?.customerUnit)}
                                </div>
                              </Col>
                              <Col xs={12} md={8}>
                                <Text type="secondary">{t('orders:supplierOrders.allocation.marginQuantity')}</Text>
                                <div style={{ fontWeight: 650, marginTop: 6, color: '#1d4ed8' }}>
                                  {number(balance.allocatedSupplierQuantity)} {formatUnit(allocation?.unit)}
                                </div>
                              </Col>
                            </Row>

                            {balance.error && (
                              <Text type="danger" style={{ display: 'block', marginTop: 10 }}>
                                {t('orders:supplierOrders.allocation.missingDimensions')}
                              </Text>
                            )}

                            {hasSurplus && !balance.error && (
                              <Form.Item
                                name={[field.name, 'surplusDisposition']}
                                label={t('orders:supplierOrders.allocation.surplus', {
                                  quantity: number(balance.surplusInCustomerUnit),
                                  unit: formatUnit(allocation?.customerUnit),
                                })}
                                style={{ margin: '12px 0 0' }}
                              >
                                <Radio.Group>
                                  <Space wrap>
                                    <Radio value="STOCK">{t('orders:supplierOrders.allocation.toStock')}</Radio>
                                    <Radio value="CUSTOMER">{t('orders:supplierOrders.allocation.toCustomer')}</Radio>
                                    <Radio value="WASTE">{t('orders:supplierOrders.allocation.toWaste')}</Radio>
                                  </Space>
                                </Radio.Group>
                              </Form.Item>
                            )}
                          </div>
                        );
                      }}
                    </Form.Item>
                  </Card>
                ))}
                <Form.ErrorList errors={errors} />
              </>
            )}
          </Form.List>
          <Form.Item name="notes" label={t('orders:notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={linkSubmitting}>
                {t('common:save')}
              </Button>
              <Button onClick={() => setLinkModalVisible(false)}>{t('common:cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierOrderDetail;
